

import React from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { Button } from "@/shared/ui/button";
import {
    Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/shared/ui/dialog";
import { Input } from "@/shared/ui/input";
import {
    Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage,
} from "@/shared/ui/form";
import { Switch } from "@/shared/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/shared/ui/select";
import { Truck, TruckInsert, TruckUpdate } from '@/types';

const TRUCK_TYPES = ["2t", "4t"];

// バリデーションスキーマ (ER図に合わせて修正)
const formSchema = z.object({
    name: z.string().min(1, { message: "トラック名は必須です。" }).max(50, { message: "トラック名は50文字以内で入力してください。" }),
    truck_type: z.string().min(1, { message: "トラック種別は必須です。" }),
    daily_cost: z.number().nullable().refine(val => val === null || val >= 0, { message: "日単価は0以上の数値を入力してください。" }),
    capacity_ton: z.number().min(0, { message: "積載量は0以上の数値を入力してください。" }).optional(),
    is_active: z.boolean(),
});

type TruckFormData = z.infer<typeof formSchema>;

/**
 * トラックフォームダイアログのprops
 */
interface TruckFormDialogProps {
    isOpen: boolean;
    onClose: () => void;
    onSubmit: (data: TruckInsert | TruckUpdate) => Promise<boolean>;
    defaultValues?: Truck | null;
    isUpdate: boolean;
}

/**
 * トラックフォームダイアログ
 * @param isOpen ダイアログが開いているかどうか
 * @param onClose ダイアログを閉じる
 * @param onSubmit フォームの送信処理
 * @param defaultValues デフォルト値
 * @param isUpdate 更新かどうか
 * @returns トラックフォームダイアログ
 */
export default function TruckFormDialog({ isOpen, onClose, onSubmit, defaultValues, isUpdate }: TruckFormDialogProps) {
    const form = useForm<TruckFormData>({
        resolver: zodResolver(formSchema),
        defaultValues: {
            name: defaultValues?.name ?? '',
            truck_type: defaultValues?.truck_type ?? '',
            daily_cost: defaultValues?.daily_cost ?? null,
            is_active: defaultValues?.is_active ?? true,
        },
    });

    React.useEffect(() => {
        form.reset({
            name: defaultValues?.name ?? '',
            truck_type: defaultValues?.truck_type ?? '',
            daily_cost: defaultValues?.daily_cost ?? null,
            is_active: defaultValues?.is_active ?? true,
        });
    }, [defaultValues, form]);

    /**
     * フォームの送信処理
     * @param values フォームの値
     */
    const handleFormSubmit = async (values: TruckFormData) => {
        const dataToSubmit = isUpdate && defaultValues?.id
            ? { ...values, id: defaultValues.id, updated_by: 'current-user' } as TruckUpdate
            : {
                ...values,
                tenant_id: 'default-tenant',
                capacity_ton: values.capacity_ton || 0,
                created_by: 'current-user',
                updated_by: 'current-user'
            } as TruckInsert;
        const success = await onSubmit(dataToSubmit);
        if (success) {
            form.reset();
            onClose();
        }
    };

    /**
     * トラックフォームダイアログの表示
     * @returns トラックフォームダイアログ
     */
    return (
        <Dialog open={isOpen} onOpenChange={onClose}>
            <DialogContent className="sm:max-w-[425px]">
                <DialogHeader>
                    <DialogTitle>{isUpdate ? 'トラック情報の編集' : '新規トラックの追加'}</DialogTitle>
                    <DialogDescription>
                        {isUpdate ? 'トラックの情報を更新します。' : '新しいトラックの情報を入力してください。'}
                    </DialogDescription>
                </DialogHeader>
                <Form {...form}>
                    <form onSubmit={form.handleSubmit(handleFormSubmit)} className="space-y-4 py-4">
                        <FormField
                            control={form.control}
                            name="name"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel>トラック名</FormLabel>
                                    <FormControl>
                                        <Input placeholder="2tトラックA" {...field} />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                        <FormField
                            control={form.control}
                            name="truck_type"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel>トラック種別</FormLabel>
                                    <Select onValueChange={field.onChange} defaultValue={field.value}>
                                        <FormControl>
                                            <SelectTrigger>
                                                <SelectValue placeholder="種別を選択" />
                                            </SelectTrigger>
                                        </FormControl>
                                        <SelectContent>
                                            {TRUCK_TYPES.map((type) => (
                                                <SelectItem key={type} value={type}>{type}</SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                        <FormField
                            control={form.control}
                            name="daily_cost"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel>日単価 (円)</FormLabel>
                                    <FormControl>
                                        {/* null許容のため、空文字列をnullに変換 */}
                                        <Input
                                            type="number"
                                            placeholder="5000"
                                            {...field}
                                            value={field.value ?? ''}
                                            onChange={e => field.onChange(e.target.value === '' ? null : Number(e.target.value))}
                                        />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                        <FormField
                            control={form.control}
                            name="is_active"
                            render={({ field }) => (
                                <FormItem className="flex flex-row items-center justify-between rounded-lg border p-3 shadow-sm">
                                    <div className="space-y-0.5">
                                        <FormLabel>有効状態</FormLabel>
                                        <FormDescription>
                                            このトラックを現在使用可能にするか設定します。
                                        </FormDescription>
                                    </div>
                                    <FormControl>
                                        <Switch
                                            checked={field.value}
                                            onCheckedChange={field.onChange}
                                        />
                                    </FormControl>
                                </FormItem>
                            )}
                        />
                        <DialogFooter>
                            <Button type="button" variant="outline" onClick={onClose}>キャンセル</Button>
                            <Button type="submit" disabled={form.formState.isSubmitting}>
                                {form.formState.isSubmitting ? '保存中...' : (isUpdate ? '更新' : '追加')}
                            </Button>
                        </DialogFooter>
                    </form>
                </Form>
            </DialogContent>
        </Dialog>
    );
} 